<template>
  <div class="partner-edit">
    <v-toolbar color="page-toolbar">
      <v-icon
        class="mr-8"
        @click="$router.push({ name: 'admin-partner-list' })"
        title="Retour à la liste des partenaires"
      >
        mdi-arrow-left
      </v-icon>
      <v-toolbar-title class="text-body-large" v-if="screen === 'edit'">
        Modifier un partenaire <small>(Administration)</small>
      </v-toolbar-title>
      <v-toolbar-title class="text-body-large" v-else>
        Ajouter un partenaire <small>(Administration)</small>
      </v-toolbar-title>
    </v-toolbar>
    <v-btn
      class="mt-n7 fab-absolute-right"
      icon
      size="large"
      color="error"
      @click="showDeleteConfirmation = true"
      v-if="screen === 'edit'"
    >
      <v-icon> mdi-delete </v-icon>
    </v-btn>
    <!-- Confirmation Dialog -->
    <v-dialog v-model="showDeleteConfirmation" persistent max-width="600px">
      <v-card>
        <v-card-title class="text-headline-small">Confirmer la suppression</v-card-title>
        <v-card-text> Êtes-vous sûr de vouloir supprimer ce partenaire ? Cette action est irréversible. </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="showDeleteConfirmation = false">Annuler</v-btn>
          <v-btn color="red-darken-1" variant="text" @click="confirmDelete">Confirmer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Form for editing partner details -->
    <v-form>
      <v-container>
        <h2>Information sur le partenaire</h2>
        <v-row class="mb-3" no-gutters>
          <v-col md="6" class="d-flex align-center">
            <v-text-field v-model="partner.name" label="Nom" required class="flex-grow-1"></v-text-field>
            <v-switch v-model="partner.active" label="Actif" color="green-darken-3" class="ml-4"></v-switch>
            <v-switch v-model="partner.isPaying" label="Payant" color="amber-darken-3" class="ml-4"></v-switch>
          </v-col>
        </v-row>
        <v-row class="mb-3" no-gutters>
          <v-col md="6">
            <v-textarea v-model="partner.description" label="Description" required></v-textarea>
            <v-text-field v-model="partner.url" label="URL" required class="mt-3"></v-text-field>
            <v-date-input
              :model-value="isoDateToDate(partner.expiresAt)"
              label="Date d'expiration (optionnel)"
              clearable
              hint="Laisser vide si le partenaire n'expire pas"
              persistent-hint
              class="mt-3"
              @update:model-value="partner.expiresAt = dateToIsoDate($event)"
            ></v-date-input>
            <v-file-input
              v-model="imageFile"
              label="Télécharger une image"
              accept="image/*"
              @update:model-value="uploadImage"
              variant="outlined"
            ></v-file-input>
            <v-img v-if="partner.image" :src="partner.image" max-width="444" class="mt-3 mb-6"></v-img>
            <v-spacer></v-spacer>
            <v-btn
              variant="flat"
              color="primary"
              :loading="isSaving"
              :disabled="isSaving"
              @click="updatePartner(partner)"
              v-if="screen === 'edit'"
            >
              <v-icon>mdi-pencil</v-icon>
              &nbsp; Modifier les informations du partenaire
            </v-btn>
            <v-btn
              variant="flat"
              color="primary"
              :loading="isSaving"
              :disabled="isSaving"
              @click="addPartner(partner)"
              v-if="screen === 'add'"
            >
              <v-icon>mdi-plus</v-icon>
              &nbsp; Ajouter le partenaire
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>

  </div>
</template>

<script>
  import firebase from "firebase/compat/app";
  import "firebase/compat/storage"; // Ensure Firebase storage is imported
  import imageCompression from "browser-image-compression"; // Import the image compression library
  import { dateToIsoDate, isoDateToDate } from "@/utils/dates";
  import { notifyError } from "@/services/Notification.service";

  export default {
    name: "PartnerEdit",
    data() {
      return {
        screen: "edit",
        partnerId: this.$route.params.slug,
        partner: {
          id: "",
          name: "",
          description: "",
          image: "",
          active: false,
          url: "",
          isPaying: false,
          expiresAt: null,
        },
        showDeleteConfirmation: false,
        imageFile: null,
        isSaving: false,
      };
    },
    created() {
      if (this.partnerId && this.partnerId !== "new") {
        this.loadPartnerInfo();
      } else {
        this.partner = {
          id: "",
          name: "",
          description: "",
          image: "",
          active: true,
          url: "",
          isPaying: false,
          expiresAt: null,
        };
        this.screen = "add";
      }
    },
    methods: {
      dateToIsoDate,
      isoDateToDate,
      async loadPartnerInfo() {
        try {
          const partner = await this.getPartnerInfo();
          if (!partner) {
            notifyError("Partenaire introuvable.");
            return;
          }
          this.partner = partner;
          this.partner.id = this.partnerId;
        } catch (error) {
          console.error("Error loading partner:", error);
          notifyError("Identifiant de partenaire invalide.");
        }
      },
      async getPartnerInfo() {
        return await this.$store.dispatch("PartnersService/getPartner", this.partnerId);
      },
      async addPartner(partner) {
        this.isSaving = true;
        try {
          const { id, ...partnerData } = partner;
          await this.$store.dispatch("PartnersService/addPartner", partnerData);
          this.redirectToPartnerList();
        } catch (error) {
          console.error("Error adding partner:", error);
          notifyError("Impossible d'ajouter le partenaire. Veuillez reessayer.");
        } finally {
          this.isSaving = false;
        }
      },
      async updatePartner(partner) {
        this.isSaving = true;
        try {
          await this.$store.dispatch("PartnersService/updatePartner", partner);
          this.redirectToPartnerList();
        } catch (error) {
          console.error("Error updating partner:", error);
          notifyError("Impossible de sauvegarder les modifications du partenaire.");
        } finally {
          this.isSaving = false;
        }
      },
      async confirmDelete() {
        await this.deletePartner(this.partnerId);
        this.showDeleteConfirmation = false; // Close the dialog after confirmation
        this.redirectToPartnerList();
      },
      async deletePartner(partnerId) {
        try {
          await this.$store.dispatch("PartnersService/deletePartner", partnerId);
        } catch (error) {
          console.error("Error deleting partner:", error);
          notifyError("Impossible de supprimer le partenaire.");
        }
      },
      redirectToPartnerList() {
        this.$router.push({ name: "admin-partner-list" });
      },
      async uploadImage() {
        if (!this.imageFile) return;

        try {
          // Resize the image
          const options = {
            maxWidthOrHeight: 444, // Set the maximum width or height for the image
            useWebWorker: true,
          };
          const compressedFile = await imageCompression(this.imageFile, options);

          // Use a unique, sanitized file name to avoid collisions and invalid paths.
          const originalName = compressedFile.name || this.imageFile.name || "partner-image.jpg";
          const safeFileName = originalName.replace(/[^a-zA-Z0-9._-]/g, "_");
          const path = `partners/${Date.now()}-${safeFileName}`;
          const storageRef = firebase.storage().ref();
          const imageRef = storageRef.child(path);
          const snapshot = await imageRef.put(compressedFile, {
            contentType: compressedFile.type || this.imageFile.type || "image/jpeg",
          });
          const imageUrl = await snapshot.ref.getDownloadURL();

          this.partner.image = imageUrl;
          console.log("Image uploaded successfully:", imageUrl);
        } catch (error) {
          console.error("Error uploading image:", {
            code: error && error.code,
            message: error && error.message,
            serverResponse: error && error.serverResponse,
            customData: error && error.customData,
          });

          if (error && error.code === "storage/unauthorized") {
            notifyError("Permission refusee pour televerser ou lire l'image dans Firebase Storage.");
            return;
          }

          notifyError("Echec du televersement de l'image. Consultez la console pour le detail Firebase.");
        }
      },
    },
  };
</script>
